import { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import { ThemeProvider } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { correttoTheme } from 'styles/theme-corretto'
import { azul, brand, fontFamily, semantic } from 'styles/tokens'

import { AgencyServiceInterface } from 'common/services/agency/interfaces'
import { getTenantSlug } from 'common/tenant'

import Logo from 'components/ui/logo'

/**
 * `true` quando a tela de acesso pertence a plataforma (host raiz, como
 * `app.*` ou localhost) e nao ao endereco de uma imobiliaria.
 */
export function isPlatformHost(): boolean {
  return !getTenantSlug()
}

interface AuthShellProps {
  children: ReactNode
  agency?: AgencyServiceInterface | null
  /** Esconde logo e rodape (ex.: carregando permissoes). */
  bare?: boolean
  showBackLink?: boolean
}

// Casa do simbolo, so o contorno, como marca d'agua do fundo azul
function HouseWatermark() {
  return (
    <Box
      component='svg'
      viewBox='0 0 100 100'
      aria-hidden
      sx={{
        position: 'absolute',
        width: { xs: 520, md: 760 },
        height: { xs: 520, md: 760 },
        left: { xs: -180, md: -140 },
        bottom: { xs: -200, md: -220 },
        pointerEvents: 'none'
      }}
    >
      <path
        d='M66 24 L48 11 L13 37 V80 Q13 88 21 88 H77 Q85 88 85 80 V66'
        fill='none'
        stroke='rgba(255, 255, 255, 0.05)'
        strokeWidth={8}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M31 58 L45 72 L86 31'
        fill='none'
        stroke='rgba(31, 180, 110, 0.10)'
        strokeWidth={8}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </Box>
  )
}

function BackLink({ color }: { color: string }) {
  return (
    <Box sx={{ mt: 3, textAlign: 'center' }}>
      <Link
        component={RouterLink}
        to='/'
        underline='hover'
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color,
          fontSize: '0.875rem'
        }}
      >
        <ArrowBackIcon fontSize='small' />
        Voltar ao início
      </Link>
    </Box>
  )
}

const cardSx = {
  position: 'relative',
  width: '100%',
  maxWidth: 480,
  backgroundColor: brand.branco,
  borderRadius: 3,
  p: { xs: 3, sm: 4 }
} as const

/**
 * Moldura das telas de acesso (login, esqueci a senha, criar/trocar senha).
 *
 * No host da plataforma a tela e da Corretto: fundo azul com a casa vazada,
 * cartao branco com o logo. No host de uma imobiliaria ela continua com o logo
 * e as cores dela (decisao D1 do plano CORRETTO), com "Tecnologia Corretto"
 * discreto no rodape.
 */
export default function AuthShell({
  children,
  agency,
  bare = false,
  showBackLink = true
}: AuthShellProps) {
  if (isPlatformHost()) {
    // O login fica dentro do SiteLayout, que aplica a cor e a fonte da
    // imobiliaria; aqui a tela volta a ser da Corretto
    return (
      <ThemeProvider theme={correttoTheme}>
        <Box
          sx={{
            fontFamily,
            position: 'relative',
            overflow: 'hidden',
            minHeight: '100vh',
            backgroundColor: brand.azul,
            backgroundImage: `radial-gradient(circle at 85% 10%, ${azul[700]} 0%, ${brand.azul} 55%)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 4, sm: 6 },
            px: 2
          }}
        >
          <HouseWatermark />
          <Box
            sx={{ ...cardSx, boxShadow: '0 24px 64px rgba(5, 14, 30, 0.45)' }}
          >
            {!bare && (
              <Box
                component={RouterLink}
                to='/'
                sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}
              >
                <Logo height={44} alt='Corretto' />
              </Box>
            )}
            {children}
          </Box>
          {!bare && (
            <Typography
              sx={{
                position: 'relative',
                mt: 3,
                fontSize: 12,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: azul[200]
              }}
            >
              Gestão imobiliária do jeito certo
            </Typography>
          )}
          {!bare && showBackLink && <BackLink color={azul[100]} />}
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f7f8fa',
        backgroundImage:
          'radial-gradient(circle, rgba(0,0,0,0.065) 1.2px, transparent 1.2px)',
        backgroundSize: '22px 22px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 4, sm: 6 },
        px: 2
      }}
    >
      {!bare && (
        <Box
          component={RouterLink}
          to='/'
          sx={{
            mb: 4,
            display: 'block',
            lineHeight: 0,
            textDecoration: 'none',
            color: 'inherit'
          }}
        >
          {agency?.logoUrl ? (
            <Box
              component='img'
              src={agency.logoUrl}
              alt={agency.name || 'Logo'}
              sx={{ maxHeight: 60, maxWidth: 240, objectFit: 'contain' }}
            />
          ) : (
            <Typography
              component='span'
              sx={{ fontSize: 24, fontWeight: 800, color: 'primary.main' }}
            >
              {agency?.name}
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          ...cardSx,
          borderTop: '3px solid',
          borderTopColor: 'primary.main',
          borderLeft: '1px solid rgba(0,0,0,0.06)',
          borderRight: '1px solid rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.10)'
        }}
      >
        {children}
      </Box>
      {!bare && showBackLink && <BackLink color={semantic.textSecondary} />}
      {!bare && (
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            color: semantic.textSecondary,
            fontSize: 12
          }}
        >
          <Logo layout='simbolo' height={16} alt='' />
          Tecnologia Corretto
        </Box>
      )}
    </Box>
  )
}
